// Dependencies
import { isAfter, isBefore, subYears } from 'date-fns';
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
// Types
import { List } from '../objects/list';
import { Error } from '../objects/error';
import { Column } from '../objects/column';
import { Preferences } from '../../profile/objects/preferences';
import { RoomingListState } from '../objects/roomingListState';
import { RoomingList } from '../objects/roomingList';
import { Provider } from '../../provider/objects/provider';
import { ManualAccommodation } from '../../manualAccommodation/objects/manualAccommodation';
import { RoomingListCustomProduct } from '../objects/roomingListCustomProduct';
import { DestinationReduced } from '../../destination/objects/destinationReduced';
import { ProductTyesSelect } from '../objects/productTypesSelect';
import { RoomingListEditStep } from '../objects/roomingListEditStep';
import { RoomingListFile } from '../objects/roomingListFile';
import { EMPTY_FILE_HISTORY_PAGINATION_CRITERIA } from '../constants/emptyFileHistoryPaginationCriteria';
import { FileHistoryPaginationCriteria } from '../objects/fileHistoryPaginationCriteria';

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const defaultColumn: Column[] = [{
    label: 'name',
    field_name: 'NAME',
    checked: true,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
},{
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: true,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: true,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const initialState: RoomingListState = {
    cancel_modal: false,
    columns: defaultColumn,
    count: 0,
    create: false,
    custom_products: [],
    custom_products_error: '',
    days_offset: null,
    days_offset_error: '',
    destinations: [],
    destinations_error: '',
    // document_type: 'CSV',
    document_type: 'EXCEL',
    document_type_error: '',
    edit: false,
    edit_step: RoomingListEditStep.INFO,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    extract_end_date: null,
    extract_end_date_error: '',
    extract_period_end: null,
    extract_period_end_error: '',
    extract: false,
    extract_start_date: null,
    extract_start_date_error: '',
    extract_period_start: null,
    extract_period_start_error: '',
    file_history: null,
    file_history_count: 0,
    file_history_loading: false,
    file_history_pagination_criteria: EMPTY_FILE_HISTORY_PAGINATION_CRITERIA,
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_name: '',
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    filter_status: [],
    hotel_giata: [],
    hotel_giata_error: '',
    hours: [],
    hours_error: '',
    keep_file_duration: null,
    keep_file_duration_error: '',
    limit: 50,
    list: null,
    month_days: [],
    month_days_error: '',
    name: '',
    name_error: '',
    nb_days_before_departure: null,
    nb_days_before_departure_error: '',
    order: 'asc',
    order_by: 'name',
    page: 0,
    preferences: [],
    product_end_date: null,
    product_end_date_error: '',
    product_start_date: null,
    product_start_date_error: '',
    providers: [],
    providers_error: '',
    refresh: false,
    resend_unchanged: false,
    rooming_list: null,
    running: false,
    search: '',
    week_days: [],
    week_days_error: '',
    product_types: [],
    product_types_select: 'specific-products',
};

export const roomingListSlice = createSlice({
    name: 'rooming_list',
    initialState,
    reducers: {
        addPreferences: (state, action: PayloadAction<Preferences[]>) => {
            state.preferences = [...state.preferences, ...action.payload];
        },
        loadFileHitory: (state: RoomingListState): RoomingListState => ({
            ...state, file_history_loading: true
        }),
        moveDownColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_last_author = '';
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_name = '';
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
            state.filter_status = [];
        },
        resetRoomingList: (state) => {
            state.cancel_modal = false;
            state.create = false;
            state.custom_products = [];
            state.custom_products_error = '';
            state.days_offset = null,
            state.days_offset_error = '';
            state.destinations = [];
            state.destinations_error = '';
            // state.document_type = 'CSV',
            state.document_type = 'EXCEL',
            state.document_type_error = '';
            state.edit = false;
            state.extract = false;
            state.extract_end_date = null;
            state.extract_end_date_error = '';
            state.extract_period_end= null,
            state.extract_period_end_error= '',
            state.extract_start_date = null;
            state.extract_start_date_error = '';
            state.extract_period_start = null;
            state.extract_period_start_error = '';
            state.hotel_giata = [],
            state.hotel_giata_error = '';
            state.hours = [];
            state.hours_error = '';
            state.keep_file_duration = null;
            state.keep_file_duration_error = '';
            state.name = '';
            state.name_error = '';
            state.nb_days_before_departure = null,
            state.nb_days_before_departure_error = '';
            state.product_end_date = null;
            state.product_end_date_error = '';
            state.product_start_date = null;
            state.product_start_date_error = '';
            state.providers = [];
            state.providers_error = '';
            state.resend_unchanged = false;
            state.week_days = [];
            state.week_days_error = '';
            state.month_days = [];
            state.month_days_error = '';
            state.product_types = [];
            state.product_types_select = 'specific-products';
        },
        setEditStep: (state: RoomingListState, action: PayloadAction<RoomingListEditStep>): RoomingListState => ({
            ...state, edit_step: action.payload
        }),
        setErrors: (state, action: PayloadAction<Error[]>) => {
            const errors = action.payload;
            errors.map((error: Error) => {
                state[error.key] = error.error;
            });
        },
        setExportType: (state, action: PayloadAction<'CSV' | 'PDF'>) => {
            state.export_type = action.payload;
        },
        setFileHitory: (state: RoomingListState, action: PayloadAction<null | {results: RoomingListFile[], count: number}>): RoomingListState => ({
            ...state,
            file_history: action.payload ? action.payload.results : null, file_history_loading: false,
            file_history_count:action.payload? action.payload.count: 0
        }),
        setFileHistoryPaginationCriteria: (state: RoomingListState, action: PayloadAction<FileHistoryPaginationCriteria>): RoomingListState => ({
            ...state,
            file_history_pagination_criteria: action.payload
        }),
        setLimit: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action: PayloadAction<List>) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc': 'asc';
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.page = action.payload;
        },
        setRoomingList: (state, action: PayloadAction<RoomingList>) => {
            state.edit = false;
            state.rooming_list = action.payload;
        },
        setRoomingListPreferences: (state, action: PayloadAction<Preferences[]>) => {
            state.preferences = action.payload;
        },
        setRunning: (state, action: PayloadAction<boolean>) => {
            state.running = action.payload;
        },
        setSearch: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        toggleCancelModal: (state) => {
            state.cancel_modal = !state.cancel_modal;
        },
        toggleColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleCreate: (state) => {
            state.create = !state.create;
        },
        toggleExtract: (state) => {
            state.extract = !state.extract;
        },
        toggleEdit: (state) => {
            state.edit = !state.edit;
            state.cancel_modal = false;
            state.custom_products_error = '';
            state.days_offset_error = '',
            state.destinations_error = '';
            state.document_type_error = '';
            state.extract_end_date_error = '';
            state.extract_start_date_error = '';
            state.hotel_giata_error = '';
            state.keep_file_duration_error = '';
            state.name_error = '';
            state.nb_days_before_departure_error = '';
            state.product_end_date_error = '';
            state.product_start_date_error = '';
            state.providers_error = '';
            state.hours_error = '';
            state.week_days_error = '';
            state.month_days_error = '';
            if(state.rooming_list !== null) {
                const { rooming_list } = state;
                state.name = rooming_list.name ?? '';
                state.custom_products = rooming_list.custom_products;
                state.days_offset = rooming_list.days_offset;
                state.destinations = rooming_list.destinations;
                state.document_type = rooming_list.document_type;
                state.extract_end_date = rooming_list.extract_end_date;
                state.extract_start_date = rooming_list.extract_start_date;
                state.hotel_giata = rooming_list.hotel_giata;
                state.hours = rooming_list.hours;
                state.keep_file_duration = rooming_list.keep_file_duration;
                state.nb_days_before_departure = rooming_list.nb_days_before_departure;
                state.product_end_date = rooming_list.product_end_date;
                state.product_start_date = rooming_list.product_start_date;
                state.providers = rooming_list.providers;
                state.resend_unchanged = rooming_list.resend_unchanged;
                state.week_days = rooming_list.week_days;
                state.month_days = rooming_list.month_days;
                state.product_types = rooming_list.product_types;
                if(rooming_list.product_types.length === 0) {
                    state.product_types_select = 'specific-products';
                } else if(rooming_list.product_types.includes(0)) {
                    state.product_types_select = 'all-accommodations';
                } else {
                    state.product_types_select = 'all-custom-products';
                }
            }
            /** @todo: fill up the content */
        },
        toggleExport: (state, action: PayloadAction<boolean>) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        toggleResendUnchanged: (state) => {
            state.resend_unchanged = !state.resend_unchanged;
        },
        updateCustomProducts: (state, action: PayloadAction<RoomingListCustomProduct[]>) => {
            state.custom_products = action.payload;
            state.custom_products_error = '';
        },
        updateDaysOffset: (state, action: PayloadAction<null | number>) => {
            state.days_offset = action.payload;
            state.days_offset_error = '';
        },
        updateDestinations: (state, action: PayloadAction<null | DestinationReduced[]>) => {
            state.destinations = action.payload;
            state.destinations_error = '';
        },
        updateDocumentType: (state, action: PayloadAction<'EXCEL'>) => {
            state.document_type = action.payload;
            state.document_type_error = '';
        },
        updateExtractEndDate: (state, action: PayloadAction<null | Date>) => {
            state.extract_end_date = action.payload;
            state.extract_end_date_error = '';
        },
        updateExtractPeriodEnd: (state, action: PayloadAction<null | Date>) => {
            state.extract_period_end = action.payload;
            state.extract_period_end_error = '';
        },
        updateExtractStartDate: (state, action: PayloadAction<null | Date>) => {
            state.extract_start_date = action.payload;
            state.extract_start_date_error = '';
        },
        updateExtractPeriodStart: (state, action: PayloadAction<null | Date>) => {
            state.extract_period_start = action.payload;
            state.extract_period_start_error = '';
        },
        updateFilterCreatedDateGte: (state, action: PayloadAction<null | Date>) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action: PayloadAction<null | Date>) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreator: (state, action: PayloadAction<string>) => {
            state.filter_creator = action.payload;
        },
        updateFilterLastAuthor: (state, action: PayloadAction<string>) => {
            state.filter_last_author = action.payload;
        },
        updateFilterModifiedDateGte: (state, action: PayloadAction<null | Date>) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action: PayloadAction<null | Date>) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterName: (state, action: PayloadAction<string>) => {
            state.filter_name = action.payload;
        },
        updateFilterPeriod: (state, action: PayloadAction<'CREATION' | 'MODIFICATION'>) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte;
            state.filter_created_date_lte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte;
            state.filter_modified_date_gte = action.payload === 'CREATION' ? null : state.filter_modified_date_gte;
            state.filter_modified_date_lte = action.payload === 'CREATION' ? null : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action: PayloadAction<number>) => {
            state.filter_period_type = action.payload;
        },
        updateHotelGiata: (state, action: PayloadAction<ManualAccommodation[]>) => {
            state.hotel_giata = action.payload;
            state.hotel_giata_error = '';
        },
        updateHours: (state, action: PayloadAction<number[]>) => {
            state.hours = action.payload;
            state.hours_error = '';
        },
        updateKeepFileDuration: (state, action: PayloadAction<null | number>) => {
            state.keep_file_duration = action.payload;
            state.keep_file_duration_error = '';
        },
        updateMonthDays: (state, action: PayloadAction<number[]>) => {
            state.month_days = action.payload;
            state.month_days_error = '';
        },
        updateName: (state, action: PayloadAction<string>) => {
            state.name = action.payload;
            state.name_error = '';
        },
        updateNbDaysBeforeDeparture: (state, action: PayloadAction<null | number>) => {
            state.nb_days_before_departure = action.payload;
            state.nb_days_before_departure_error = '';
        },
        updateProductEndDate: (state, action: PayloadAction<null | Date>) => {
            state.product_end_date = action.payload;
            state.product_end_date_error = '';
        },
        updateProductStartDate: (state, action: PayloadAction<null | Date>) => {
            state.product_start_date = action.payload;
            state.product_start_date_error = '';
        },
        updateProductTypesSelect: (state, action: PayloadAction<ProductTyesSelect>) => {
            state.product_types_select = action.payload;
            switch(action.payload) {
                case 'all-accommodation-all-product':
                    state.product_types = [0, 11, 12];
                case 'all-accommodations':
                    state.product_types = [0];
                    break;
                case 'all-activities':
                    state.product_types = [12, 11];
                    break;
                case 'specific-products':
                default:
                    state.product_types =[];
                    break;
            }
        },
        updateProviders: (state, action: PayloadAction<Provider[]>) => {
            state.providers = action.payload;
            state.providers_error = '';
        },
        updateWeekDays: (state, action: PayloadAction<number[]>) => {
            state.week_days = action.payload;
            state.week_days_error = '';
        },
        setColumns: (state, action: PayloadAction<Column[]>) => {
            state.columns = action.payload;
        },
        resizeColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.columns = applyColumnResize(state.columns, action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'ROOMING_LIST') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'NAME':
                                    columns.push({ label: 'name', field_name: preference.field_name, checked: preference.active, order_by: 'name', fixed_width: false, last_updated: false });
                                    order_by = preference.ordering_asc !== null ? 'name' : order_by;
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({ label: 'created-date', field_name: preference.field_name, checked: preference.active, order_by: 'created_date', fixed_width: true, last_updated: false });
                                    order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({ label: 'created-by', field_name: preference.field_name, checked: preference.active, order_by: 'creator__username', fixed_width: false, last_updated: false });
                                    order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({ label: 'modified-date', field_name: preference.field_name, checked: preference.active, order_by: 'modified_date', fixed_width: true, last_updated: false });
                                    order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({ label: 'modified-by', field_name: preference.field_name, checked: preference.active, order_by: 'last_author__username', fixed_width: false, last_updated: false });
                                    order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map(column => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({ ...column });
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .addDefaultCase(() => {});
    }
});

export const {
    addPreferences,
    loadFileHitory,
    moveDownColumn,
    moveUpColumn,
    resetFilters,
    resetRoomingList,
    setEditStep,
    setErrors,
    setExportType,
    setFileHitory,
    setFileHistoryPaginationCriteria,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setRoomingList,
    setRoomingListPreferences,
    setRunning,
    setSearch,
    toggleCancelModal,
    toggleColumn,
    toggleCreate,
    toggleEdit,
    toggleExtract,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    toggleRefresh,
    toggleResendUnchanged,
    updateCustomProducts,
    updateDaysOffset,
    updateDestinations,
    updateDocumentType,
    updateExtractEndDate,
    updateExtractPeriodEnd,
    updateExtractStartDate,
    updateExtractPeriodStart,
    updateFilterCreatedDateGte,
    updateFilterCreatedDateLte,
    updateFilterCreator,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterName,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateHotelGiata,
    updateHours,
    updateKeepFileDuration,
    updateMonthDays,
    updateName,
    updateNbDaysBeforeDeparture,
    updateProductEndDate,
    updateProductStartDate,
    updateProductTypesSelect,
    updateProviders,
    updateWeekDays,
    setColumns,
    resizeColumn
} = roomingListSlice.actions;

export default roomingListSlice.reducer;